import { Link } from '@tanstack/react-router';
import { useEffect, useId, useState } from 'react';
import { AccountError, api } from '../account/api.ts';
import { useAccount } from '../account/store.ts';
import { formatDate } from '../files.ts';
import { Markdown } from '../help.tsx';

/**
 * The desktop app's download page (docs/DOWNLOAD-PLAN.md), for signed-in accounts: what
 * the app is, screenshots, the newest installer, what an unsigned installer means, and a
 * confirmation before the download. The server records each download.
 */

/** Where the screenshots are, beside the app (public/download/, kept out of the offline cache). */
export const SCREENSHOT_FOLDER = 'download/';

const SCREENSHOTS: readonly { file: string; width: number; height: number; caption: string }[] = [
  { file: 'atlas-player-view.png', width: 1100, height: 760, caption: 'The Atlas: a dungeon under fog of war, as the players see it' },
  { file: 'game-master-run.png', width: 1440, height: 900, caption: 'Game Master Toolkit: running an encounter, round by round' },
  { file: 'character-sheet.png', width: 1280, height: 860, caption: 'Character sheets from templates, with rolls built in' },
  { file: 'story-studio-scan.png', width: 840, height: 692, caption: 'Story Studio: finding who and what a scene mentions' },
  { file: 'knowledge-modes.png', width: 1440, height: 860, caption: 'Knowledge Modes: the realm arranged for exploring' },
  { file: 'skin-ember-night.png', width: 1180, height: 760, caption: 'Skins: the whole app restyled, here in Ember Night' },
];

interface Offer {
  available: boolean;
  /** Turned off on the admin site, for now. */
  turnedOff?: boolean;
  version?: string;
  fileName?: string;
  size?: number;
  sha256?: string;
  publishedAt?: number;
  notes?: string;
  remainingToday?: number;
}

export function DownloadPage() {
  const account = useAccount();
  return (
    <article className="document download-page">
      <p className="eyebrow">Realmery for Windows</p>
      <h1>The desktop app</h1>
      <p className="lead">
        Realmery began as a Windows app, and it still does more than the web: every realm is a file on your own computer,
        and it works with no connection at all.
      </p>
      <p>
        The realms are the same in both. Export a realm as a <code>.realmery</code> file from either one and open it in the
        other; anything one edition doesn't show yet is kept safe in the file. Only on the desktop, for now:
      </p>
      <ul className="desktop-features">
        <li>
          <strong>Grammar checking:</strong> agreement, confused words and style, as you write. Spelling and the thesaurus are
          on the web too.
        </li>
      </ul>

      <section className="screenshots" aria-label="Screenshots">
        {SCREENSHOTS.map((shot) => (
          <figure key={shot.file}>
            <a href={`/${SCREENSHOT_FOLDER}${shot.file}`} target="_blank" rel="noopener">
              <img src={`/${SCREENSHOT_FOLDER}${shot.file}`} width={shot.width} height={shot.height} alt={shot.caption} loading="lazy" />
            </a>
            <figcaption>{shot.caption}</figcaption>
          </figure>
        ))}
      </section>

      {account.status === 'loading' && <p className="meta">Loading…</p>}
      {account.status === 'unavailable' && <p className="callout">Downloading needs a connection to Realmery. Try again when you're online.</p>}
      {account.status === 'signed-out' && (
        <section className="panel">
          <h2>Sign in to download</h2>
          <p>The desktop app is for Realmery accounts. Sign in, then come back to this page.</p>
          <div className="actions">
            <Link className="button primary" to="/signin">
              Sign in
            </Link>
          </div>
        </section>
      )}
      {account.status === 'signed-in' && <Download />}
    </article>
  );
}

function Download() {
  const [offer, setOffer] = useState<Offer | null>(null);
  const [error, setError] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [working, setWorking] = useState(false);
  const [started, setStarted] = useState(false);
  const checkId = useId();

  useEffect(() => {
    api
      .get<Offer>('downloads/windows')
      .then(setOffer)
      .catch((e: unknown) => setError(e instanceof AccountError ? e.message : 'The download could not be loaded.'));
  }, []);

  async function download() {
    setWorking(true);
    setError('');
    try {
      const { url } = await api.post<{ url: string; fileName: string }>('downloads/windows', { acknowledged: true });
      // The single-use link answers with the file as an attachment, so the page stays put.
      const link = document.createElement('a');
      link.href = url;
      link.rel = 'noopener';
      document.body.append(link);
      link.click();
      link.remove();
      setStarted(true);
      setOffer((current) => current && { ...current, remainingToday: Math.max(0, (current.remainingToday ?? 1) - 1) });
    } catch (e) {
      setError(e instanceof AccountError ? e.message : 'The download could not start.');
    } finally {
      setWorking(false);
    }
  }

  if (error && !offer) return <p className="notice notice-error" role="alert">{error}</p>;
  if (!offer) return <p className="meta">Loading…</p>;
  if (!offer.available) {
    return (
      <section className="panel">
        <h2>Download for Windows</h2>
        <p>{offer.turnedOff ? 'Downloading the desktop app is turned off for now. Check back later.' : 'No version is available to download yet. Check back soon.'}</p>
      </section>
    );
  }

  const megabytes = ((offer.size ?? 0) / 1024 ** 2).toFixed(0);
  return (
    <>
      <section className="panel download-offer">
        <h2>Realmery {offer.version} for Windows</h2>
        <p>
          {offer.fileName}, {megabytes} MB, published {formatDate(offer.publishedAt ?? 0)}. For 64-bit
          Windows 10 or 11, with nothing else to install: Java and everything else it needs come with it. It installs for
          you only, without asking for an administrator, and never touches your realms when it's updated or removed.
        </p>
        {offer.notes && (
          <details>
            <summary>What's new in {offer.version}</summary>
            <div className="release-notes">
              <Markdown source={offer.notes} shift={2} />
            </div>
          </details>
        )}
      </section>

      <section className="panel unsigned-notice">
        <h2>This installer isn't code-signed</h2>
        <p>
          Big software companies sign their installers with a certificate that tells Windows who made them. Realmery's
          isn't signed yet: a certificate costs several hundred dollars a year. The installer is safe, but Windows can't
          confirm where it came from, so you'll see some warnings.
        </p>
        <ul>
          <li>
            <strong>Your browser</strong> may say the file "isn't commonly downloaded". Choose <strong>Keep</strong> (in Edge,
            it's under the download's <strong>…</strong> menu).
          </li>
          <li>
            <strong>Windows SmartScreen</strong> says "Windows protected your PC" the first time you run it. Choose{' '}
            <strong>More info</strong>, then <strong>Run anyway</strong>.
          </li>
          <li>
            <strong>Some antivirus programs</strong> may warn about a new unsigned program, or hold it back for a scan.
          </li>
        </ul>
        <p>To be sure the file is the one published here, download it only from this page, and check it before running it:</p>
        <ol>
          <li>Open PowerShell in your Downloads folder.</li>
          <li>
            Run <code>Get-FileHash .\{offer.fileName}</code>
          </li>
          <li>
            The <strong>Hash</strong> it shows must match this one, letter for letter:
            <code className="sha256">{offer.sha256?.toUpperCase()}</code>
          </li>
        </ol>
      </section>

      <section className="panel download-confirm">
        <label htmlFor={checkId} className="check">
          <input id={checkId} type="checkbox" checked={understood} onChange={(event) => setUnderstood(event.currentTarget.checked)} />
          <span>
            I understand that this installer isn't code-signed, that Windows and my browser may warn me about it, and how to
            check the file before running it.
          </span>
        </label>
        <div className="actions">
          <button type="button" className="primary" disabled={!understood || working || offer.remainingToday === 0} onClick={() => void download()}>
            {working ? 'Starting…' : 'Download for Windows'}
          </button>
        </div>
        <p className="meta">Downloads are recorded with your account. You can download the app {offer.remainingToday === 0 ? 'again tomorrow' : 'up to 10 times a day'}.</p>
        {started && (
          <p className="notice" role="status">
            The download has started. When it's finished, run {offer.fileName} and follow the steps above.
          </p>
        )}
        {error && (
          <p className="notice notice-error" role="alert">
            {error}
          </p>
        )}
      </section>
    </>
  );
}
